import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { assetMembershipApi } from 'src/Services/API';
import {
  AssetMemberDto,
  AssetMembersDto,
  PermissionEntry,
  UpdateMemberPermissionsDto,
  UpdateMemberPermissionsDtoPresetEnum,
} from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { ApiError } from 'src/types/api-error.types';

type UpdateMemberPermissionsParams = {
  membershipId: string;
  permissions?: PermissionEntry[];
  preset?: UpdateMemberPermissionsDtoPresetEnum;
};

type UpdateMemberPermissionsContext = {
  previousMembers?: AssetMembersDto;
};

export const useUpdateMemberPermissions = (
  options?: UseMutationOptions<
    AssetMemberDto,
    AxiosError<ApiError>,
    UpdateMemberPermissionsParams,
    UpdateMemberPermissionsContext
  >
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();
  const queryKey = ['fetch-members', assetId];

  return useMutation<
    AssetMemberDto,
    AxiosError<ApiError>,
    UpdateMemberPermissionsParams,
    UpdateMemberPermissionsContext
  >(
    async (mutationParams: UpdateMemberPermissionsParams) => {
      if (!assetId) {
        throw new Error('AssetId is required for updating member permissions.');
      }
      const { membershipId, permissions, preset } = mutationParams;
      const updateDto: UpdateMemberPermissionsDto = { permissions, preset };
      const response = await assetMembershipApi.membersControllerUpdatePermissions(
        membershipId,
        assetId,
        updateDto
      );
      return response.data;
    },
    {
      mutationKey: ['update-member-permissions', assetId],
      onMutate: async (
        _variables: UpdateMemberPermissionsParams
      ): Promise<UpdateMemberPermissionsContext> => {
        await queryClient.cancelQueries({ queryKey });
        const previousMembers = queryClient.getQueryData<AssetMembersDto>(queryKey);
        return { previousMembers };
      },
      onError: (
        err: AxiosError<ApiError>,
        variables: UpdateMemberPermissionsParams,
        context?: UpdateMemberPermissionsContext
      ) => {
        if (context?.previousMembers) {
          queryClient.setQueryData(queryKey, context.previousMembers);
        }
        options?.onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey });
      },
      onSuccess: (data, variables, context) => {
        trackEvent('Membership', 'Update Permissions', variables.membershipId);
        queryClient.invalidateQueries(['fetch-asset-details', assetId]);
        options?.onSuccess?.(data, variables, context);
      },
      ...options,
    }
  );
};
